import type { Step, Option, Question } from '@de/types/types';
import type { FlowState } from '../providers/dynamicExperienceReducer';
import type { DcsKey } from '@de/utils/taxonomy/fetchTaxonomy';
import { buildBoundComponentIndex } from '@de/utils/configurable-step/findBoundComponent';

export interface AutoPopulationContext {
  zipCode?: string;
  dcs?: { degree?: string; category?: string; subject?: string };
  entryStepId?: string;
  /**
   * The step a deep-link landing resolves `currentStep` to (the `jumpto`
   * URL param a `/discover` eject carries). When present and distinct from
   * the DCS seed target, the DCS triad is mirrored onto this step as well so
   * a deep-linked result step's dimension `<Select>`s pre-fill — see the
   * seed block below for why the entry-step-only seed isn't enough.
   */
  jumpToStepId?: string;
  location?: { state?: string; zipCode?: string; countryCode?: string };
  /**
   * Optional slug→label resolver for the DCS triad. When provided, the
   * seeded option's display `text` is set to the resolved label while
   * `value` continues to carry the slug. Used so a flow auto-populated from
   * a `/discover` eject (slugs in the URL) shows human labels in copy
   * (question placeholders, chips) without leaking the label onto the
   * slug-keyed wire values. Returns the slug unchanged on a miss.
   */
  dcsTranslate?: (key: DcsKey, slug: string) => string;
  /**
   * Optional customField→slug map from a Discover host.
   * Seeded onto the owning step / nested `stepQuestions` child by
   * `customField` — never stamped with `leadFieldAssociated`.
   * Named to align with `DynamicExperienceContextProviderProps.forwardedContext`.
   */
  forwardedContext?: Record<string, string>;
}

type CustomFieldOwner = {
  id: string;
  /**
   * Set only for DSL / Configurable Step ownership. Seeds retain
   * `Option.customField` so multi-control steps can disambiguate.
   */
  optionCustomField?: string;
  /**
   * Authored options on a step-/question-owned field. Used to resolve the
   * real Option so QUESTION UIs (Dropdown, Radio, …) that select by
   * `option.optionId` can pre-mark the matching choice.
   */
  options?: Option[];
};

/**
 * Finds the step, nested `stepQuestions` child, or DSL control that owns
 * `customField`. Explicit step/question ownership wins. Configurable Steps
 * declare ownership on a form control's `_meta.customField`; those seeds are
 * stored under the parent step id and retain `Option.customField` so readers
 * can disambiguate multiple controls sharing that flowState slot.
 */
const findCustomFieldOwner = (
  steps: Step[],
  customField: string,
  boundComponentIndexes: Map<Step, ReturnType<typeof buildBoundComponentIndex>>,
): CustomFieldOwner | undefined => {
  for (const step of steps) {
    const nested =
      (step as Step & { stepQuestions?: Question[] }).stepQuestions ?? [];
    for (const child of nested) {
      if (child.customField === customField && child.id) {
        return { id: child.id, options: child.options };
      }
    }
    if (step.customField === customField && step.id) {
      return {
        id: step.id,
        options: (step as Step & { options?: Option[] }).options,
      };
    }
  }

  for (const step of steps) {
    if (!step.id) continue;
    const dslConfig = (
      step as Step & {
        dslConfig?: { data?: unknown };
      }
    ).dslConfig;
    const content = (dslConfig?.data as { content?: unknown } | undefined)
      ?.content;
    if (!content) continue;

    let boundIndex = boundComponentIndexes.get(step);
    if (!boundIndex) {
      boundIndex = buildBoundComponentIndex(content);
      boundComponentIndexes.set(step, boundIndex);
    }
    const ownsCustomField = Object.values(boundIndex).some(
      (bound) => bound.customField === customField,
    );
    if (ownsCustomField) {
      return { id: step.id, optionCustomField: customField };
    }
  }

  return undefined;
};

/**
 * Builds the Option written into flowState for a forwarded customField.
 *
 * DSL-owned fields keep `optionId: customField` (plus `Option.customField`)
 * so ConfigurableStep readers can disambiguate shared step slots.
 *
 * Step-/question-owned fields prefer the authored Option whose `slug`,
 * `value`, or `optionId` matches the forwarded slug — QUESTION variants
 * bind selection UI to `option.optionId`, so a synthetic id like the
 * customField handle would never highlight. When no authored option
 * matches, fall back to `optionId: slug` (not the handle).
 */
const resolveCustomFieldSeedOption = (
  owner: CustomFieldOwner,
  customField: string,
  slug: string,
): Option => {
  if (owner.optionCustomField) {
    return {
      optionId: customField,
      text: slug,
      value: slug,
      customField: owner.optionCustomField,
    };
  }

  const options = owner.options ?? [];
  const authored =
    options.find((opt) => opt.slug != null && String(opt.slug) === slug) ??
    options.find((opt) => opt.value != null && String(opt.value) === slug) ??
    options.find((opt) => String(opt.optionId) === slug);

  if (authored) {
    return { ...authored };
  }

  return {
    optionId: slug,
    text: slug,
    value: slug,
  };
};

/**
 * Merges a customField seed into an existing flowState slot without wiping
 * prior DCS / zip auto-population data on the same step id.
 */
const writeCustomFieldSeed = (
  flowState: FlowState,
  ownerId: string,
  seedOption: Option,
): void => {
  const existing = flowState[ownerId];
  if (!existing) {
    flowState[ownerId] = { option: seedOption };
    return;
  }

  const existingOpt = existing.option as Option | Option[] | undefined;
  if (!existingOpt) {
    flowState[ownerId] = { ...existing, option: seedOption };
    return;
  }

  if (Array.isArray(existingOpt)) {
    const idx = existingOpt.findIndex(
      (o) =>
        o.optionId === seedOption.optionId ||
        o.customField === seedOption.optionId,
    );
    if (idx >= 0) {
      const next = [...existingOpt];
      next[idx] = seedOption;
      flowState[ownerId] = { ...existing, option: next };
    } else {
      flowState[ownerId] = {
        ...existing,
        option: [...existingOpt, seedOption],
      };
    }
    return;
  }

  if (existingOpt.optionId === seedOption.optionId) {
    flowState[ownerId] = { ...existing, option: seedOption };
    return;
  }

  flowState[ownerId] = {
    ...existing,
    option: [existingOpt, seedOption],
  };
};

/**
 * Auto-populates flow state with values from global context (e.g., ZIP code from location)
 * @param steps - Array of flow steps
 * @param context - Global context with values to auto-populate
 * @returns Flow state with auto-populated values
 */
export const initializeAutoPopulation = (
  steps: Step[],
  context: AutoPopulationContext,
): FlowState => {
  const flowState: FlowState = {};

  // Handle DCS data (degree/category/subject slugs the host injects as
  // `window.HE.contextualDefaults`). Seed whatever subset is present:
  // a partial DCS (e.g. degree only on an article page) should still
  // pre-fill the field it knows rather than seeding nothing.
  if (context.dcs && Object.values(context.dcs).some(Boolean)) {
    const entryOptions: Option[] = (
      Object.keys(context.dcs) as Array<keyof NonNullable<typeof context.dcs>>
    )
      .filter((key) => Boolean(context.dcs![key]))
      .map((key) => {
        const value = context.dcs![key] as string;
        // Display text resolves to the human label when a translator is
        // supplied; `value` always stays the slug for slug-keyed branching,
        // dimensions, and the Offerings `/match` filter.
        const text = context.dcsTranslate
          ? context.dcsTranslate(key as DcsKey, value)
          : value;
        return {
          optionId: key,
          text,
          value,
          leadFieldAssociated: key,
        };
      });

    // Write under the *actual* entry step id so the seed lands in the
    // same flowState slot `ConfigurableStep` reads (`currentStep.id`).
    // `flowService` hoists the `isEntry` step to `steps[0]` and
    // `initializeCurrentStep` defaults `currentStep` to `steps[0]`, so
    // `steps.find(isEntry) ?? steps[0]` resolves to the same id the read
    // side sees under default wiring. The literal `'dcs'` is the
    // last-ditch fallback for the steps-less case.
    const entryStep = steps.find((step) => step.isEntry === true) ?? steps[0];
    const targetKey = context.entryStepId || entryStep?.id || 'dcs';
    flowState[targetKey] = { option: entryOptions };

    // Deep-link landings: a `/discover` eject builds its handoff URL with
    // `from=<entryStepId>` and `jumpto=<resultStepId>`, so `currentStep`
    // resolves to the jump-to (result) step while the seed above lands on
    // the entry step. `ConfigurableStep` derives its DSM `formState` seed
    // from `currentStep` *only*, so without mirroring the triad here the
    // deep-linked result step's dimension `<Select>`s start empty even
    // though degree/category/subject rode in on the URL. The DCS triad is a
    // global contextual default (not a per-step user selection), so seeding
    // it onto the step the user actually lands on is safe. Scoped to jump-to
    // landings — normal in-flow navigation carries no `jumpToStepId` and is
    // unaffected. A fresh array avoids sharing a mutable reference across
    // the two flowState slots.
    if (context.jumpToStepId && context.jumpToStepId !== targetKey) {
      flowState[context.jumpToStepId] = { option: [...entryOptions] };
    }
  }

  // Seed Discover-forwarded customFields onto the owning step / nested
  // question. Kept separate from the DCS block so triad options retain
  // `leadFieldAssociated` + `dcsTranslate` behavior. No jump-to mirror:
  // without LFA, ConfigurableStep formState derivation cannot consume a
  // mirrored customField seed; when the landing step owns the field,
  // ownership lookup already writes to that slot.
  if (context.forwardedContext) {
    const boundComponentIndexes = new Map<
      Step,
      ReturnType<typeof buildBoundComponentIndex>
    >();
    for (const [customField, slug] of Object.entries(
      context.forwardedContext,
    )) {
      if (!customField || typeof slug !== 'string' || !slug.trim()) {
        continue;
      }
      const owner = findCustomFieldOwner(
        steps,
        customField,
        boundComponentIndexes,
      );
      if (!owner) continue;

      writeCustomFieldSeed(
        flowState,
        owner.id,
        resolveCustomFieldSeedOption(owner, customField, slug),
      );
    }
  }

  // Handle field-based auto-population (ZIP code, etc.)
  const populateField = (field: {
    id?: string;
    leadFieldAssociated?: string | keyof import('@de/types/types').Lead;
  }) => {
    if (typeof field.leadFieldAssociated !== 'string') return;

    let autoValue: string | undefined;

    const fieldName = field.leadFieldAssociated as string;
    if (
      (fieldName === 'zip' || fieldName === 'zipCode') &&
      (context.zipCode || context.location?.zipCode)
    ) {
      autoValue = context.zipCode || context.location?.zipCode;
    }

    if (autoValue) {
      const option: Option = {
        optionId: `${field.leadFieldAssociated}-auto-input`,
        text: autoValue,
        value: autoValue,
        group: `${field.leadFieldAssociated}-auto-input`,
      };
      flowState[field.id || ''] = {
        option,
        leadFieldAssociated: field.leadFieldAssociated,
      };
    }
  };

  steps.forEach((step) => {
    populateField(step);
    const subQuestions = (step as any).stepQuestions ?? [];
    subQuestions.forEach((sq: any) => populateField(sq));
  });

  if (context.location) {
    const { state, zipCode, countryCode } = context.location;
    flowState['location'] = {
      state: state?.toUpperCase() || '',
      ipInferredPostalCode: zipCode || '',
      // Upper-cased ISO country code (e.g. "US"). Surfaced as a flat
      // `formState.countryCode` by `deriveInitialFormState` so DSL data
      // sources can bind it as the offerings budget `geolocation`.
      countryCode: countryCode?.toUpperCase() || '',
    };
  }

  return flowState;
};
